import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { forwardRef, useState } from 'react';
import ControlBarInsights from './ControlBarInsights';
import * as styles from './ControlBarInsightsButton.module.scss';
import ControlBarPopOver from './ControlBarPopOver';
import AvatarStack from '../components/AvatarStack';
import Button from '../components/Button';
import LoadingIndicator from '../components/LoadingIndicator';
import { gql } from '../graphql/generated';
import type { DocumentType } from '../graphql/generated/gql';
import { useGetUser } from '../user/useAllUsers';
import useIsLayoutSize from './utils/useIsLayoutSize';
import { LayoutSize } from './utils/useLayoutSize';

interface ControlBarButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active: boolean;
  children: React.ReactNode;
  controlButtonVariant?: 'warning' | 'info' | 'default';
}

export const ControlBarButton = forwardRef<
  HTMLButtonElement,
  ControlBarButtonProps
>(
  (
    { active, children, className, controlButtonVariant = 'default', ...rest },
    ref,
  ) => (
    <Button
      {...rest}
      ref={ref}
      className={classNames(className, styles.button, {
        [styles.active]: active,
        [styles.danger]: controlButtonVariant === 'warning',
        [styles.info]: controlButtonVariant === 'info',
      })}
    >
      {children}
    </Button>
  ),
);

export const ControlBarIconButton = forwardRef<
  HTMLButtonElement,
  ControlBarButtonProps
>(({ active, children, ...rest }, ref) => (
  <Button
    ref={ref}
    {...rest}
    className={classNames(styles.iconButton, {
      [styles.active]: active,
    })}
  >
    {children}
  </Button>
));

const POST_FRAGMENT = gql(`
  fragment ControlBarInsightsButton_post on Post {
    id
    owner {
      id
      ...useGetUser_user
    }
    contributors {
      id
      ...useGetUser_user
    }
  }
`);

const POST_QUERY = gql(/** @complexity 14 */ `
  query ControlBarInsightsButtonPost($id: ID!) {
    post(id: $id) {
      id
      ...ControlBarInsights_post
    }
  }
`);

interface ControlBarInsightsButtonProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  children: React.ReactNode;
  popOverPlacement: React.ComponentProps<typeof ControlBarPopOver>['placement'];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ControlBarInsightsButton = ({
  post,
  children,
  popOverPlacement,
  open,
  onOpenChange,
}: ControlBarInsightsButtonProps) => {
  const [isShowingConfirmationPopOver, setIsShowingConfirmationPopOver] =
    useState(false);
  const isMobile = useIsLayoutSize(LayoutSize.mobile);

  const getUser = useGetUser();
  const users = [
    post.owner,
    ...post.contributors.filter(
      (contributor) => contributor.id !== post.owner.id,
    ),
  ].map(getUser);

  const [shouldLoadPopOverData, setShouldLoadPopOverData] = useState(false);

  const { data } = useQuery(POST_QUERY, {
    skip: !shouldLoadPopOverData && !open,
    variables: { id: post.id },
  });

  const avatarStack = (
    <div className={styles.avatarStackContainer}>
      <AvatarStack
        size={22}
        limit={4}
        items={users}
        spacing="tight"
        borderSize={2}
        countOverflowVariant="smallPill"
        noWrap
      />
    </div>
  );

  return (
    <div className={styles.insightsButtonContainer}>
      {isMobile ? (
        <div className={styles.mobileInsightsInfo}>
          {avatarStack}
          <span>{children}</span>
        </div>
      ) : (
        <ControlBarPopOver
          onOpenChange={(nextOpen) => {
            if (!isShowingConfirmationPopOver) {
              onOpenChange(nextOpen);
            }
          }}
          hide={isShowingConfirmationPopOver}
          content={
            <div className={styles.contentContainer}>
              {data?.post ? (
                <ControlBarInsights
                  post={data.post}
                  close={() => onOpenChange(false)}
                  setIsShowingConfirmationPopOver={
                    setIsShowingConfirmationPopOver
                  }
                />
              ) : (
                <div className={styles.loading}>
                  <LoadingIndicator />
                </div>
              )}
            </div>
          }
          placement={popOverPlacement}
          open={open}
        >
          <ControlBarButton
            active={open}
            onPointerDown={() => {
              setShouldLoadPopOverData(true);
            }}
          >
            <ControlBarPopOver.Anchor>{avatarStack}</ControlBarPopOver.Anchor>
            <span>{children}</span>
          </ControlBarButton>
        </ControlBarPopOver>
      )}
    </div>
  );
};

export default ControlBarInsightsButton;
